// Detect numbered headings and slice the document into sections.
//
// The naive regex matches far more than headings in a real engineering plan:
// table-of-contents entries, numbered procedure steps ("1 Define the desired
// precision..."), street addresses ("24630 Corporate Road"), and table rows
// beginning with a figure. Three general filters do the work — front-matter
// detection, segment shape, and sequence validation — none of which is tuned to
// a particular document.
//
// Never throws. With no confident headings, the whole document is one section.

import type { Section } from '../../types';
import type { PageLayout } from './pdfExtract';
import { isRunningLine } from './pdfExtract';

const HEADING = /^\s*(\d+(?:\.\d+)*)\.?[\s ]+([A-Z][^\n]{3,80})$/;

/** A page carrying several dot-leader lines is a table of contents. */
function isFrontMatterPage(page: PageLayout): boolean {
  const leaders = page.lines.filter((l) => /\.{4,}/.test(l.text)).length;
  return leaders >= 4;
}

/** Section numbers are small. "24630" and "500" are not section numbers. */
function isPlausibleNumber(number: string): boolean {
  const parts = number.split('.');
  if (parts.length > 4) return false;
  return parts.every((p) => p.length <= 2 && p.length > 0);
}

/**
 * Sequence validation. A heading must follow the last accepted one: a new
 * top-level section is the previous plus one, a sibling increments, and a child
 * starts at 1. A small forward tolerance absorbs a heading we failed to read
 * without letting arbitrary numbers through.
 */
function isPlausibleNext(prev: number[] | null, next: number[]): boolean {
  if (prev === null) {
    // The first heading should open the document, not arrive mid-way.
    return next[0] <= 2 && next.slice(1).every((n) => n <= 2);
  }

  // Deeper: a first child, e.g. 3.2 -> 3.2.1.
  if (next.length > prev.length) {
    if (next.length !== prev.length + 1) return false;
    if (!prev.every((p, i) => p === next[i])) return false;
    return next[next.length - 1] <= 2;
  }

  // A top-level section must be exactly the previous plus one. No tolerance:
  // this is what separates "4 ECM 2 — EMCS..." from a numbered procedure step
  // ("5 Fixture powers will be based on...") sitting inside section 3.
  const tolerance = next.length === 1 ? 1 : 2;

  // Same level: increment, tolerating one missed sibling below the top level.
  if (next.length === prev.length) {
    const parentSame = prev.slice(0, -1).every((p, i) => p === next[i]);
    if (!parentSame) return false;
    const delta = next[next.length - 1] - prev[prev.length - 1];
    return delta >= 1 && delta <= tolerance;
  }

  // Shallower: closing a subsection, e.g. 3.2.4 -> 3.3, or 3.7 -> 4.
  const parentSame = prev.slice(0, next.length - 1).every((p, i) => p === next[i]);
  if (!parentSame) return false;
  const delta = next[next.length - 1] - prev[next.length - 1];
  return delta >= 1 && delta <= tolerance;
}

const CONTINUES = /(?:,|\band\b|\bor\b|\bof\b|\bthe\b|\bfor\b|&)$/i;

function isAllCaps(text: string): boolean {
  const letters = text.replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return false;
  return letters === letters.toUpperCase();
}

/** A heading whose title wrapped onto the following line. */
function joinContinuation(title: string, nextLine: string | undefined): string {
  if (!nextLine) return title;
  const next = nextLine.trim();
  if (!next || next.length > 80 || HEADING.test(next)) return title;
  // All-caps heading continued by an all-caps line.
  if (isAllCaps(title) && isAllCaps(next)) return `${title} ${next}`;
  // Title-case heading left hanging on a conjunction.
  if (!isAllCaps(title) && CONTINUES.test(title.trim()) && /^[A-Z]/.test(next)) {
    return `${title} ${next}`;
  }
  // A word or two left over on the next line: "...(O&M) Rate" + "Data". A
  // sentence of body text does not look like this — it is longer, or it ends in
  // punctuation.
  //
  // The continuation must contain a letter. A bare numeral on the line below a
  // heading is far more often a footnote marker than part of the title, and
  // joining it would turn "Status of Rebates" into "Status of Rebates 4". A
  // title truncated at a wrap is a smaller error than a title with something
  // added that the document does not say.
  const words = next.split(/\s+/);
  if (
    words.length <= 2 &&
    next.length <= 24 &&
    !/[.:;!?]$/.test(next) &&
    /^[A-Z(]/.test(next) &&
    /[A-Za-z]/.test(next)
  ) {
    return `${title} ${next}`;
  }
  return title;
}

function normalise(title: string): string {
  return title
    .replace(/\s*\.{4,}.*$/, '') // stray dot leader
    .replace(/\t+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
}

interface Heading {
  number: string;
  parts: number[];
  title: string;
  page: number;
  lineIndex: number;
}

export function findHeadings(
  pages: PageLayout[],
  running: Set<string>,
): Heading[] {
  const accepted: Heading[] = [];
  let prev: number[] | null = null;

  for (const page of pages) {
    if (isFrontMatterPage(page)) continue;

    for (let i = 0; i < page.lines.length; i++) {
      const line = page.lines[i];
      const text = line.text;

      if (isRunningLine(text, running)) continue;
      if (/\.{4,}/.test(text)) continue;

      const match = HEADING.exec(text.replace(/\t/g, ' '));
      if (!match) continue;

      const [, number, rawTitle] = match;
      if (!isPlausibleNumber(number)) continue;

      // A heading is a label, not a sentence. Numbered list items end in a
      // full stop; headings do not.
      if (/\.\s*$/.test(rawTitle)) continue;

      const parts = number.split('.').map(Number);
      if (!isPlausibleNext(prev, parts)) continue;

      // A heading sits on its own line. If the "title" carries a column break,
      // this is a table row, not a heading.
      if (line.cells.length > 2) continue;

      const title = normalise(
        joinContinuation(rawTitle, page.lines[i + 1]?.text),
      );
      if (!title) continue;

      accepted.push({
        number,
        parts,
        title,
        page: page.number,
        lineIndex: i,
      });
      prev = parts;
    }
  }

  return accepted;
}

/**
 * A numbering scheme should govern the document, not a corner of it. Three
 * numbered items starting on page 31 of 48 are a recommendations list, not a
 * section structure — a document like that is better described as one section
 * than as three wrong ones.
 */
function coversDocument(headings: Heading[], pages: PageLayout[]): boolean {
  if (headings.length === 0) return false;
  if (pages.length <= 3) return true;
  if (headings.length < 3) return false;

  const first = headings[0].page;
  const last = pages[pages.length - 1].number;
  const firstIndex = pages.findIndex((p) => p.number === first);
  // The structure has to start somewhere in the front half of the document.
  return firstIndex >= 0 && firstIndex / pages.length < 0.5 && last >= first;
}

export function parseSections(
  pages: PageLayout[],
  running: Set<string>,
): Section[] {
  const found = findHeadings(pages, running);
  const headings = coversDocument(found, pages) ? found : [];
  const body = (page: PageLayout) =>
    page.lines.filter((l) => !isRunningLine(l.text, running));

  if (headings.length === 0) {
    // Degrade, do not throw.
    return [
      {
        id: 'section-document',
        number: '',
        title: 'Document',
        startPage: pages.length ? pages[0].number : 1,
        endPage: pages.length ? pages[pages.length - 1].number : 1,
        text: pages.map((p) => body(p).map((l) => l.text).join('\n')).join('\n\n'),
      },
    ];
  }

  const pageByNumber = new Map(pages.map((p) => [p.number, p]));
  const lastPage = pages[pages.length - 1].number;

  return headings.map((h, i) => {
    const next = headings[i + 1];
    const endPage = next ? next.page : lastPage;

    // Text runs from this heading to the next, across page boundaries.
    const chunks: string[] = [];
    for (let p = h.page; p <= endPage; p++) {
      const page = pageByNumber.get(p);
      if (!page) continue;
      const lines = body(page);
      const from = p === h.page ? h.lineIndex : 0;
      const to = next && p === next.page ? next.lineIndex : lines.length;
      // lineIndex is an index into the unfiltered lines; map by text identity.
      const startText = page.lines[from]?.text;
      const endText = next && p === next.page ? page.lines[to]?.text : undefined;
      const startAt = startText ? Math.max(0, lines.findIndex((l) => l.text === startText)) : 0;
      const endAt = endText ? lines.findIndex((l) => l.text === endText) : lines.length;
      chunks.push(
        lines
          .slice(startAt, endAt === -1 ? lines.length : endAt)
          .map((l) => l.text)
          .join('\n'),
      );
    }

    return {
      id: `section-${h.number.replace(/\./g, '-')}`,
      number: h.number,
      title: h.title,
      startPage: h.page,
      endPage,
      text: chunks.join('\n').trim(),
    };
  });
}
